import { toast } from 'react-toastify';
import { redirectToPersonalBillingPage } from './helpers';
import { PlansStore } from './plans';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import {
  cancelPersonalSubscription,
  cancelPersonalSubscriptionDowngrade,
  cancelPersonalSubscriptionUpdate,
  renewPersonalSubscription,
} from '~/lib/membership/membershipApi';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { userStore } from '~/lib/stores/user';
import { createProCheckoutSession } from '~/lib/subscription/subscriptionApi';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { UserData } from '~/types/user';
import { personalPricingList, type Pricing } from '~/utils/pricing';

class ProPricingPlansStore extends PlansStore {
  constructor() {
    super({
      defaultPricing: personalPricingList[0],
      pricingList: personalPricingList,
      pricingType: 'pro',
    });
  }

  initActiveState(user: UserData) {
    super._initActiveState({
      tier: user.membership?.tier ?? null,
      nextTier: user.membership?.nextTier,
      nextInterval: user.membership?.nextInterval,
      subscription: user.membership?.subscription,
    });
  }

  initInactiveState(inactivityReason: string) {
    // inactive state is when we select a team/create-team workspace

    this.displayMode = 'inactive';
    this.membership.set(null);
    this.selectedPricing.set(this.defaultPricing);

    this.actions.set([
      {
        text: inactivityReason,
        variant: 'secondary',
        disabled: true,
        id: 'inactive',
      },
    ]);

    this.messages.set([
      {
        text: 'For personal plans, switch to your personal workspace using the selector at the top left.',
        placement: 'under-action',
      },
    ]);
  }

  async manageSubscription() {
    const membership = this.membership.get();
    const user = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ManageSubscriptionClicked,
        properties: {
          // user context (who triggered the action)
          user_email: user?.email,
          user_id: user?.id,

          // plan context
          plan_type: 'pro',
          current_tier: membership?.subscription?.quantity,
          billing_interval: membership?.subscription?.interval,
          subscription_status: membership?.subscription?.status,

          // action context
          location: 'pro_pricing_store',
        },
      },
    });

    this._startRequestForPricing('manage-subscription');

    try {
      await redirectToPersonalBillingPage();
      this._endRequestForPricing();
    } catch (error) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.BillingPortalRedirectFailed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'pro',
            error_message: String(error),
          },
        },
      });
      throw error;
    }
  }

  async changePlan(pricing: Pricing, updateType: 'upgrade' | 'downgrade' | 'change_interval') {
    const membership = this.membership.get();
    const interval = pricingStore.selectedInterval.get();
    const user = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.PlanChangeStarted,
        properties: {
          // user context (who triggered the action)
          user_email: user?.email,
          user_id: user?.id,

          // plan context
          plan_type: 'pro',
          update_type: updateType,
          current_tier: membership?.subscription?.quantity,
          target_tier: pricing.tier,
          current_interval: membership?.subscription?.interval,
          target_interval: interval,
          pricing_level: pricing.level,
          current_subscription_status: membership?.subscription?.status,

          // action context
          location: 'pro_change_plan',
        },
      },
    });

    const hasSubscription = (membership?.tier ?? 0) > 0;

    if (hasSubscription) {
      pricingStore.updatePlanScreenData.set({
        updateType,
        pricing,
        interval,
      });

      return;
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const freeTrialParam = urlParams.get('free_trial');

      const sessionUrl = await createProCheckoutSession(pricing.level, interval, freeTrialParam ?? undefined);

      window.open(sessionUrl, '_self');
    } catch (error) {
      const user = userStore.getUser();

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.PlanChangeFailed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'pro',
            action_type: hasSubscription ? 'update_session' : 'checkout_session',
            error_message: String(error),
            target_tier: pricing.tier,
          },
        },
      });

      toast.error(`Failed to initiate ${hasSubscription ? 'update' : 'checkout'}. Please try again later.`);
    } finally {
      this._endRequestForPricing();
    }
  }

  async cancelSubscription() {
    const membership = this.membership.get();
    const user = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.CancelSubscriptionClicked,
        properties: {
          // user context
          user_email: user?.email,
          user_id: user?.id,

          // plan context
          plan_type: 'pro',
          current_tier: membership?.subscription?.quantity,
          billing_interval: membership?.subscription?.interval,
          subscription_status: membership?.subscription?.status,

          // action context
          location: 'pro_cancel_subscription',
        },
      },
    });

    this._startRequestForPricing('cancel-subscription');

    try {
      await cancelPersonalSubscription();

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SubscriptionCancellationConfirmed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'pro',
            current_tier: membership?.subscription?.quantity,
          },
        },
      });
    } catch (error) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.PlanChangeFailed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'pro',
            action_type: 'cancel_subscription',
            error_message: String(error),
          },
        },
      });
      throw error;
    } finally {
      this._endRequestForPricing();
    }
  }

  async cancelSubscriptionUpdate() {
    this._startRequestForPricing('cancel-subscription-update');

    try {
      await cancelPersonalSubscriptionUpdate();
    } finally {
      this._endRequestForPricing();
    }
  }

  async renewSubscription() {
    await renewPersonalSubscription();
  }

  async cancelDowngrade(pricing: Pricing) {
    this._startRequestForPricing('cancel-downgrade');

    try {
      await cancelPersonalSubscriptionDowngrade(pricing.level);
    } finally {
      this._endRequestForPricing();
    }
  }
}

export const proPricingPlansStore = new ProPricingPlansStore();

pricingStore.selectedInterval.subscribe((interval) => {
  proPricingPlansStore.onIntervalChange(interval);
});

userStore.user.subscribe((user) => {
  if (!user?.membership) {
    return;
  }

  const tier = user.membership.tier ?? null;
  const nextTier = user.membership.nextTier ?? null;

  proPricingPlansStore.membership.set({
    tier,
    nextTier,
    nextInterval: user.membership.nextInterval,
    subscription: user.membership.subscription,
  });

  if (tier) {
    proPricingPlansStore.activePricing.set(personalPricingList.find((pricing) => pricing.tier === tier) ?? null);
  }
});

if (import.meta.env.DEV) {
  Object.assign(globalThis, { proPricingPlansStore });
}
